import { useState, useRef, useCallback, useEffect } from 'react';
import { ArrowLeft, MapPin, Camera, Package, MessageCircle } from 'lucide-react';
import { Go7StyleNavMap, type NavRouteStats } from '../map/Go7StyleNavMap';
import { TripBottomSheet } from '../map/TripBottomSheet';
import { dbService } from '../../services/firebase-db.service';
import { useSmoothGps } from '../../hooks/useSmoothGps';
import { isValidJobCoords, buildGoogleMapsDirectionsUrlToPoint } from '../../utils/jobCoordinates';

interface Job {
  id: string;
  pickupName: string;
  pickupAddress: string;
  pickupPhone?: string;
  pickupLocation?: { lat: number; lng: number };
  deliveryName: string;
  deliveryAddress: string;
  deliveryLocation?: { lat: number; lng: number };
  distance: string;
  estimatedEarnings: string;
  estimatedTime: string;
  packageType?: string;
  items?: string[];
  customerName: string;
  customerPhone: string;
  requiresPin?: boolean;
  status: 'going-to-pickup' | 'at-pickup' | 'going-to-delivery' | 'at-delivery';
  fleetType: 'rider' | 'bicycle' | 'leg-fleet';
}

interface ActiveJobScreenProps {
  job: Job;
  riderId?: string;
  onBack: () => void;
  autoStartNav?: boolean;
  onArrivedAtPickup: () => void;
  onOpenPickupConfirmation: () => void;
  onArrivedAtDelivery: () => void;
  onOpenChat?: () => void;
}

export function ActiveJobScreen({
  job,
  riderId,
  onBack,
  autoStartNav,
  onArrivedAtPickup,
  onOpenPickupConfirmation,
  onArrivedAtDelivery,
  onOpenChat,
}: ActiveJobScreenProps) {
  const [routeStats, setRouteStats] = useState<NavRouteStats | null>(null);
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const lastLocationSyncRef = useRef(0);
  const lastPosRef = useRef<{ lat: number; lng: number } | null>(null);
  const hasSyncedInitialRef = useRef(false);

  useEffect(() => {
    if (autoStartNav) setSheetExpanded(true);
  }, [autoStartNav]);

  const syncRiderLocation = useCallback(
    (location: { lat: number; lng: number }) => {
      if (!job.id) return;
      const now = Date.now();
      const isFirstFix = !hasSyncedInitialRef.current;
      const elapsed = now - lastLocationSyncRef.current;
      const movedM = lastPosRef.current
        ? 6371000 * 2 * Math.asin(Math.sqrt(
            Math.sin(((location.lat - lastPosRef.current.lat) * Math.PI) / 360) ** 2 +
            Math.cos((lastPosRef.current.lat * Math.PI) / 180) *
            Math.cos((location.lat * Math.PI) / 180) *
            Math.sin(((location.lng - lastPosRef.current.lng) * Math.PI) / 360) ** 2
          ))
        : Infinity;
      if (!isFirstFix && (elapsed < 12000 || (elapsed < 60000 && movedM < 25))) return;
      lastLocationSyncRef.current = now;
      lastPosRef.current = location;
      hasSyncedInitialRef.current = true;
      void dbService.updateRiderLocation(job.id, location, riderId).catch((err) => {
        console.error('Failed to update rider location:', err);
      });
    },
    [job.id, riderId]
  );

  const gps = useSmoothGps(true, syncRiderLocation);
  const hasGps = Boolean(gps?.hasFix && gps?.position?.lat && gps?.position?.lng);
  const currentLocation = hasGps ? gps!.position! : undefined;

  // Same as go7 destinationMode: pickup until heading to delivery
  const isHeadingToDelivery =
    job.status === 'going-to-delivery' || job.status === 'at-delivery';
  const navDestination = isHeadingToDelivery ? job.deliveryLocation : job.pickupLocation;
  const hasValidNavTarget = isValidJobCoords(navDestination);
  const navAddress = isHeadingToDelivery ? job.deliveryAddress : job.pickupAddress;

  const getStatusInfo = () => {
    switch (job.status) {
      case 'going-to-pickup':
        return {
          title: 'Heading to pickup',
          subtitle: job.pickupAddress,
          action: 'Arrived at Pickup',
          onAction: onArrivedAtPickup,
          icon: MapPin,
          color: '#E0001B',
        };
      case 'at-pickup':
        return {
          title: 'At pickup — confirm package',
          subtitle: job.pickupAddress,
          action: 'Take Pickup Photos',
          onAction: onOpenPickupConfirmation,
          icon: Camera,
          color: '#E0001B',
        };
      case 'going-to-delivery':
        return {
          title: 'Heading to delivery',
          subtitle: job.deliveryAddress,
          action: 'Arrived at Delivery',
          onAction: onArrivedAtDelivery,
          icon: MapPin,
          color: '#00B277',
        };
      case 'at-delivery':
        return {
          title: 'At delivery location',
          subtitle: job.deliveryAddress,
          action: 'Complete Delivery',
          onAction: onArrivedAtDelivery,
          icon: Package,
          color: '#00B277',
        };
      default:
        return {
          title: 'Active Delivery',
          subtitle: '',
          action: 'Continue',
          onAction: () => {},
          icon: Package,
          color: '#E0001B',
        };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  const openGoogleMapsNav = () => {
    const dest = hasValidNavTarget
      ? navDestination!
      : isHeadingToDelivery
        ? job.deliveryLocation
        : job.pickupLocation;
    if (!isValidJobCoords(dest)) {
      alert('Destination coordinates missing. Try again in a moment.');
      return;
    }
    const url = buildGoogleMapsDirectionsUrlToPoint(
      dest!,
      hasGps ? currentLocation : null
    );
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const hasMapCoords = isHeadingToDelivery
    ? isValidJobCoords(job.pickupLocation) && isValidJobCoords(job.deliveryLocation)
    : isValidJobCoords(job.pickupLocation);
  const mapDeliveryLocation = isValidJobCoords(job.deliveryLocation)
    ? job.deliveryLocation!
    : job.pickupLocation!;

  const stats = {
    distanceLabel: routeStats?.distanceLabel || job.distance,
    etaLabel: routeStats?.etaLabel || job.estimatedTime,
    earnings: job.estimatedEarnings,
  };

  return (
    <div className="absolute inset-0 bg-[#0a0a0a] flex flex-col overflow-hidden">
      <div className="flex-1 relative min-h-0 overflow-hidden">
        <div className="absolute inset-0">
          {hasMapCoords ? (
            <Go7StyleNavMap
              pickupLocation={{
                lat: job.pickupLocation!.lat,
                lng: job.pickupLocation!.lng,
                address: job.pickupAddress,
              }}
              deliveryLocation={{
                lat: mapDeliveryLocation.lat,
                lng: mapDeliveryLocation.lng,
                address: job.deliveryAddress,
              }}
              currentLocation={
                currentLocation
                  ? { ...currentLocation, heading: gps?.heading ?? 0 }
                  : undefined
              }
              navTarget={
                hasValidNavTarget
                  ? {
                      lat: navDestination!.lat,
                      lng: navDestination!.lng,
                      address: navAddress,
                    }
                  : undefined
              }
              destinationLabel={navAddress}
              riderHeading={gps?.heading ?? 0}
              riderSpeedMps={gps?.speedMps ?? 0}
              accuracyM={gps?.accuracyM ?? null}
              isMoving={gps?.isMoving ?? false}
              onRouteStatsUpdate={setRouteStats}
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center bg-gray-100 p-6 text-center gap-3">
              <MapPin className="w-8 h-8 text-[#E0001B]" />
              <p className="text-sm text-gray-600">
                Waiting for job map coordinates. Stay in the app — navigation will appear when ready.
              </p>
            </div>
          )}

          {!hasGps && hasMapCoords && (
            <div className="absolute top-24 left-1/2 -translate-x-1/2 z-20 px-4 w-full max-w-sm">
              <div className="bg-black/85 text-white text-xs px-3 py-2.5 rounded-xl backdrop-blur-sm shadow-lg text-center space-y-2">
                {gps?.error === 'denied' ? (
                  <>
                    <p className="font-medium">Location blocked</p>
                    <p className="text-white/75 text-[11px]">
                      Allow location for go7.ng in your browser settings, then tap retry.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium">Getting your location…</p>
                    <p className="text-white/75 text-[11px]">
                      Allow location when prompted so navigation can follow you like Google Maps.
                    </p>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => gps?.retry?.()}
                  className="pointer-events-auto w-full py-2 rounded-lg bg-[#E0001B] text-white text-[12px] font-semibold"
                >
                  Retry GPS
                </button>
              </div>
            </div>
          )}
        </div>

        <div
          className="absolute top-0 left-0 right-0 z-30 pointer-events-none"
          style={{ paddingTop: 'max(12px, env(safe-area-inset-top))' }}
        >
          <div className="flex items-start justify-between px-3 pt-3 gap-2">
            <button
              type="button"
              onClick={onBack}
              className="pointer-events-auto w-11 h-11 rounded-full bg-black/55 backdrop-blur-md border border-white/20 flex items-center justify-center shrink-0"
              aria-label="Back to dashboard"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>

            <div className="pointer-events-none flex-1 flex justify-center min-w-0 px-1">
              <div className="bg-[#111111]/90 backdrop-blur-md text-white px-3 py-2 rounded-full flex items-center gap-3 shadow-xl max-w-full">
                <div className="text-center min-w-0">
                  <p className="text-[9px] uppercase tracking-wider text-white/60">ETA</p>
                  <p className="text-[13px] font-semibold truncate">{stats.etaLabel}</p>
                </div>
                <div className="w-px h-5 bg-white/20 shrink-0" />
                <div className="text-center min-w-0">
                  <p className="text-[9px] uppercase tracking-wider text-white/60">Dist</p>
                  <p className="text-[13px] font-semibold truncate">{stats.distanceLabel}</p>
                </div>
                <div className="w-px h-5 bg-white/20 shrink-0" />
                <div className="text-center min-w-0">
                  <p className="text-[9px] uppercase tracking-wider text-white/60">Earn</p>
                  <p className="text-[13px] font-semibold text-[#4ade80] truncate">{stats.earnings}</p>
                </div>
              </div>
            </div>

            {onOpenChat ? (
              <button
                type="button"
                onClick={onOpenChat}
                className="pointer-events-auto w-11 h-11 rounded-full bg-black/55 backdrop-blur-md border border-white/20 flex items-center justify-center shrink-0"
                aria-label="Chat with customer"
              >
                <MessageCircle className="w-5 h-5 text-white" />
              </button>
            ) : (
              <div className="w-11 shrink-0" />
            )}
          </div>
        </div>

        {job.items && job.items.length > 0 ? (
          <div className="pointer-events-auto mx-3 mb-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 shadow-sm">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-900 mb-1">
              Contents to pick
            </p>
            <ul className="space-y-0.5">
              {job.items.map((item, i) => (
                <li key={i} className="text-[13px] text-[#111111]">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <TripBottomSheet
          key={`${job.id}-${job.status}-${autoStartNav ? 'start' : 'idle'}`}
          title={statusInfo.title}
          subtitle={statusInfo.subtitle}
          statusColor={statusInfo.color}
          StatusIcon={StatusIcon}
          primaryAction={statusInfo.action}
          onPrimaryAction={statusInfo.onAction}
          onStartNavigation={openGoogleMapsNav}
          navigationLabel="Open Google Maps navigation"
          pickupName={job.pickupName}
          pickupPhone={job.pickupPhone}
          customerName={job.customerName}
          customerPhone={job.customerPhone}
          onOpenChat={onOpenChat}
          defaultExpanded={Boolean(autoStartNav) || sheetExpanded}
        />
      </div>
    </div>
  );
}
